import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import { getPayment, ReceiptResp, TYPE_LABEL, METHOD_LABEL, money, textoDolares } from '../api';
import logo from '../../../assets/logo-badge.webp';

function fmtDateTime(d: string | null) {
  if (!d) return '—';
  const dd = new Date(d);
  if (isNaN(dd.getTime())) return String(d).slice(0, 16);
  return dd.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) +
    ' · ' + dd.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function folio(r: ReceiptResp) {
  if (r.data.invoice_number) return r.data.invoice_number;
  return `${r.studio.prefix}-${String(r.data.payment_id).padStart(5, '0')}`;
}

export default function ReceiptPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState<ReceiptResp | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    getPayment(Number(id)).then(setR).catch(() => setErr('No se encontró el pago.'));
  }, [id]);

  if (err) return <div className="mx-auto max-w-md p-6 text-center text-content-3">{err}</div>;
  if (!r) return <div className="p-6 text-content-3">Cargando recibo…</div>;

  const p = r.data;
  const concepto = [TYPE_LABEL[p.type] || p.type, p.assoc_name].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-lg">
      {/* Controles (no se imprimen) */}
      <div className="no-print mb-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-content-2 hover:text-content"><ArrowLeft size={15} /> Volver</button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent transition hover:opacity-90">
          <Printer size={16} /> Imprimir / Guardar PDF
        </button>
      </div>

      {/* Recibo imprimible */}
      <div className="print-area rounded-2xl border border-line bg-surface p-8 shadow-sm">
        <div className="flex items-center gap-4 border-b border-line pb-5">
          <img src={logo} alt="" className="h-16 w-16 rounded-full bg-white object-contain ring-1 ring-line" />
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-content">{r.studio.name}</div>
            <div className="text-xs text-content-3">{r.studio.address}</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wide text-content-3">Recibo</div>
            <div className="font-display text-sm font-bold text-content">{folio(r)}</div>
          </div>
        </div>

        <div className="py-5">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Fecha</div>
          <div className="text-sm text-content">{fmtDateTime(p.datetime || p.pay_date)}</div>

          <div className="mt-4 text-xs font-medium uppercase tracking-wide text-content-3">Recibí de</div>
          <div className="text-base font-semibold text-content">{p.payer || '—'}</div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Concepto</div>
              <div className="text-sm text-content">{concepto || '—'}{p.months ? ` (${p.months})` : ''}</div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Método de pago</div>
              <div className="text-sm text-content">{p.currency === 'USD' ? 'Dólares' : METHOD_LABEL[p.method] || p.method}</div>
              {/* Cobro en dólares (y mixto, 3-nov-2026): cuánto entregó en cada moneda. */}
              {p.currency === 'USD' && (
                <div data-testid="recibo-usd" className="text-xs text-content-2">
                  {textoDolares(p.usd_amount, p.mxn_amount)}{p.fx_rate ? ` · TC ${Number(p.fx_rate)}` : ''}
                </div>
              )}
            </div>
          </div>

          {p.comment && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Comentario</div>
              <div className="text-sm text-content-2">{p.comment}</div>
            </div>
          )}
        </div>

        {/* Descuento aplicado: si el cobro llevó descuento, el recibo tiene que decirlo — si no,
            dentro de seis meses nadie sabe por qué entró menos de lo que costaba el mes. */}
        {Number(p.amount_discount) > 0 && (
          <div className="mb-2 flex items-center justify-between px-5 text-sm">
            <span className="text-content-2">{p.discount_name || 'Descuento aplicado'}</span>
            <span className="font-medium text-amber-600 dark:text-amber-400">−{money(Number(p.amount_discount))}</span>
          </div>
        )}

        {/* Total */}
        <div className="flex items-center justify-between rounded-xl bg-accent/10 px-5 py-4">
          <span className="font-display text-sm font-semibold uppercase tracking-wide text-content-2">Total pagado</span>
          <span className="font-display text-2xl font-bold text-content">{money(p.amount)}<span className="ml-1 text-sm font-medium text-content-3">MXN</span></span>
        </div>

        <div className="mt-8 flex items-end justify-between">
          <div className="text-xs text-content-3">
            <div className="mb-6">Recibido por</div>
            <div className="border-t border-line pt-1">{p.teller || '—'}</div>
          </div>
          <div className="text-right text-[10px] text-content-3">
            ¡Gracias por ser parte de Let's Move! 💛<br />Conserva este recibo como comprobante.
          </div>
        </div>
      </div>
    </div>
  );
}
